import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { getPostBySlug } from "./index";
import "../Style/Accueil.ObsidianAura.css";
import "../Style/Blog.css";

function Block({ b }) {
  if (!b) return null;

  switch (b.type) {
    case "p":
      return <p className="hiw-p">{b.text}</p>;
    case "h2":
      return <h2 className="hiw-h2 blog-h2">{b.text}</h2>;
    case "h3":
      return <h3 className="hiw-h3 blog-h3">{b.text}</h3>;
    case "ul":
      return (
        <ul className="blog-ul">
          {(b.items || []).map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      );
    case "quote":
      return <blockquote className="blog-quote">“{b.text}”</blockquote>;
    case "insight":
      return <p className="blog-insight"><strong>{b.text}</strong></p>;
    case "callout":
      return (
        <section className={`blog-callout blog-callout--${b.variant || "default"}`}>
          {(b.items || []).map((x, i) => <p key={i}>{x}</p>)}
        </section>
      );
    case "cta":
      return (
        <section className="blog-cta">
          {(b.items || []).map((item, i) => {
            const isExternal = item.href && /^https?:\/\//.test(item.href);
            const body = (
              <>
                <strong>{item.label}</strong>
                <span>{item.text}</span>
              </>
            );

            return isExternal ? (
              <a key={i} href={item.href} target="_blank" rel="noopener noreferrer">
                {body}
              </a>
            ) : (
              <Link key={i} to={item.href || "/"}>
                {body}
              </Link>
            );
          })}
        </section>
      );
    case "systemMap":
      return <SystemMap b={b} />;
    case "image":
      return (
        <figure className="blog-figure">
          <img className="blog-img" src={b.src} alt={b.alt || ""} loading="lazy" />
          {b.caption && <figcaption className="blog-caption">{b.caption}</figcaption>}
        </figure>
      );
    case "video":
      if (b.provider === "youtube") {
        return (
          <figure className="blog-figure">
            <div className="blog-videoWrap">
              <iframe
                title="YouTube video"
                src={`https://www.youtube.com/embed/${b.id}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            {b.caption && <figcaption className="blog-caption">{b.caption}</figcaption>}
          </figure>
        );
      }
      if (b.provider === "vimeo") {
        return (
          <figure className="blog-figure">
            <div className="blog-videoWrap">
              <iframe
                title="Vimeo video"
                src={`https://player.vimeo.com/video/${b.id}`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
            {b.caption && <figcaption className="blog-caption">{b.caption}</figcaption>}
          </figure>
        );
      }
      if (b.provider === "mp4") {
        return (
          <figure className="blog-figure">
            <video className="blog-video" controls playsInline preload="metadata">
              <source src={b.src} type="video/mp4" />
            </video>
            {b.caption && <figcaption className="blog-caption">{b.caption}</figcaption>}
          </figure>
        );
      }
      return null;
    default:
      return null;
  }
}

function SystemMap({ b }) {
  const [isConnected, setIsConnected] = useState(false);
  const items = b.items || [];

  return (
    <section className={`blog-systemMap ${isConnected ? "is-connected" : ""}`}>
      <div className="blog-systemMapHeader">
        <div>
          <p className="blog-systemMapKicker">{isConnected ? b.lifeLabel : b.fragmentedLabel}</p>
          <h2>{b.title}</h2>
        </div>
        <button type="button" onClick={() => setIsConnected((value) => !value)}>
          {isConnected ? "Voir les silos" : "Connecter"}
        </button>
      </div>

      <div className="blog-systemMapCanvas" aria-label="Schéma interactif des domaines de vie">
        <svg className="blog-systemMapLines" viewBox="0 0 100 100" aria-hidden="true">
          <line x1="50" y1="50" x2="35" y2="34" />
          <line x1="50" y1="50" x2="50" y2="24" />
          <line x1="50" y1="50" x2="65" y2="34" />
          <line x1="50" y1="50" x2="35" y2="66" />
          <line x1="50" y1="50" x2="50" y2="76" />
          <line x1="50" y1="50" x2="65" y2="66" />
        </svg>
        <div className="blog-systemMapHub">Life</div>
        {items.map((item, i) => (
          <span key={item} className={`blog-systemNode blog-systemNode--${i + 1}`}>
            {item}
          </span>
        ))}
      </div>
    </section>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const { i18n, t } = useTranslation("blog");

  const post = useMemo(
    () => getPostBySlug({ slug, lang: i18n.language }),
    [slug, i18n.language]
  );

  if (!post) {
    return (
      <main className="life-home blog">
        <div className="life-container">
          <div className="life-glass blog-hero">
            <h1 className="life-h1">{t("post.notFound", "Article introuvable")}</h1>
            <Link to="/blog" className="hiw-ctaSecondary">{t("post.back", "Retour au blog")}</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="life-home blog">
      <Helmet>
        <title>{post.seo?.title || post.title}</title>
        <meta name="title" content={post.seo?.title || post.title} />
        <meta name="description" content={post.seo?.description || post.excerpt} />
        {post.seo?.keywords?.length && (
          <meta name="keywords" content={post.seo.keywords.join(", ")} />
        )}
        <link rel="canonical" href={`https://be-lifer.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.seo?.title || post.title} />
        <meta property="og:description" content={post.seo?.description || post.excerpt} />
        <meta property="og:url" content={`https://be-lifer.com/blog/${post.slug}`} />
        <meta property="og:site_name" content="Be-Lifer" />
        {post.cover && <meta property="og:image" content={post.cover} />}
        <meta name="twitter:card" content={post.cover ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={post.seo?.title || post.title} />
        <meta name="twitter:description" content={post.seo?.description || post.excerpt} />
        {post.cover && <meta name="twitter:image" content={post.cover} />}
      </Helmet>
      <div className="hiw-bg" />
      <div className="life-home__overlay" />

      <section className="life-section">
        <div className="life-container">
          <article className="life-glass blog-post">
            <Link to="/blog" className="blog-back">← {t("post.back", "Retour")}</Link>

            <h1 className="life-h1">{post.title}</h1>
            {post.subtitle && <p className="life-lead blog-subtitle">{post.subtitle}</p>}
            <div className="blog-meta">
              <span>{new Date(post.date).toLocaleDateString()}</span>
              <span>•</span>
              <span>{post.readingMinutes} min</span>
            </div>

            {post.cover && (
              <img className="blog-coverHero" src={post.cover} alt={post.title} loading="lazy" />
            )}

            <div className="blog-content">
              {(post.content || []).map((b, i) => <Block key={i} b={b} />)}
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}
